import React, { useState } from 'react';
import { FaTimes, FaArrowUp, FaArrowDown, FaSortAlphaDown, FaSortAlphaUp, FaGripVertical, FaCheck } from 'react-icons/fa';
import { stripHtml } from '../../utils/styleHelpers';

export const CategoryRearrangeModal = ({
    isOpen,
    onClose,
    categories = [],
    handleReorderCategories,
    handleMoveCategory,
    handleSortCategoriesAlpha,
    primaryColor = '#3b82f6'
}) => {
    const [draggedIndex, setDraggedIndex] = useState(null);

    if (!isOpen) return null;

    // Filter out 'All' if present
    const categoryList = (categories || []).filter(c => {
        const name = typeof c === 'string' ? c : (c?.name || '');
        return name && name !== 'All';
    });

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === targetIndex) return;
        const list = categoryList.map(c => typeof c === 'string' ? c : c.name);
        const [moved] = list.splice(draggedIndex, 1);
        list.splice(targetIndex, 0, moved);
        setDraggedIndex(null);
        if (handleReorderCategories) {
            handleReorderCategories(list);
        }
    };

    const handleMoveItem = (index, direction) => {
        const list = categoryList.map(c => typeof c === 'string' ? c : (c?.name || ''));
        let targetIdx = index;
        if (direction === 'up') targetIdx = index - 1;
        if (direction === 'down') targetIdx = index + 1;
        if (targetIdx < 0 || targetIdx >= list.length) return;

        const [moved] = list.splice(index, 1);
        list.splice(targetIdx, 0, moved);

        if (handleReorderCategories) {
            handleReorderCategories(list);
        } else if (handleMoveCategory) {
            handleMoveCategory(list[targetIdx], direction);
        }
    };

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(4px)',
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px'
            }}
            onClick={onClose}
        >
            <div 
                style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    width: '100%',
                    maxWidth: '520px',
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    border: '1px solid #e2e8f0'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{
                    padding: '18px 24px',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: '#f8fafc'
                }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>↕️</span> Rearrange Category Order
                        </h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                            Use Up/Down arrows or drag items to change how categories appear on your store.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            background: '#f1f5f9',
                            border: 'none',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: '#64748b',
                            fontSize: '0.9rem',
                            transition: 'all 0.15s'
                        }}
                        title="Close modal"
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Sorting presets toolbar */}
                <div style={{
                    padding: '10px 24px',
                    background: '#ffffff',
                    borderBottom: '1px solid #f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px'
                }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#475569' }}>
                        Quick Presets:
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            type="button"
                            onClick={() => handleSortCategoriesAlpha && handleSortCategoriesAlpha(false)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '5px 12px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#1e293b',
                                backgroundColor: '#f8fafc',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                            title="Sort categories alphabetically A to Z"
                        >
                            <FaSortAlphaDown style={{ color: primaryColor }} /> A → Z
                        </button>
                        <button
                            type="button"
                            onClick={() => handleSortCategoriesAlpha && handleSortCategoriesAlpha(true)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                padding: '5px 12px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                color: '#1e293b',
                                backgroundColor: '#f8fafc',
                                border: '1px solid #cbd5e1',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                            title="Sort categories alphabetically Z to A"
                        >
                            <FaSortAlphaUp style={{ color: primaryColor }} /> Z → A
                        </button>
                    </div>
                </div>

                {/* Category List */}
                <div style={{
                    padding: '16px 24px',
                    overflowY: 'auto',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                }}>
                    {categoryList.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '0.9rem' }}>
                            No categories found to rearrange.
                        </div>
                    ) : (
                        categoryList.map((cat, index) => {
                            const name = typeof cat === 'string' ? cat : cat.name;
                            const isFirst = index === 0;
                            const isLast = index === categoryList.length - 1;

                            return (
                                <div
                                    key={name}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDrop={(e) => handleDrop(e, index)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px 14px',
                                        background: draggedIndex === index ? '#eff6ff' : '#ffffff',
                                        border: draggedIndex === index ? `2px dashed ${primaryColor}` : '1px solid #e2e8f0',
                                        borderRadius: '10px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                                        gap: '12px',
                                        cursor: 'grab',
                                        userSelect: 'none',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ color: '#94a3b8', cursor: 'grab', display: 'flex', alignItems: 'center' }} title="Drag to reorder">
                                            <FaGripVertical />
                                        </span>
                                        <span style={{
                                            fontSize: '0.75rem',
                                            fontWeight: '800',
                                            color: '#64748b',
                                            background: '#f1f5f9',
                                            borderRadius: '6px',
                                            padding: '2px 8px',
                                            minWidth: '28px',
                                            textAlign: 'center'
                                        }}>
                                            #{index + 1}
                                        </span>
                                        <span style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0f172a' }}>
                                            {stripHtml(name)}
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <button
                                            type="button"
                                            disabled={isFirst}
                                            onClick={() => handleMoveItem(index, 'up')}
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '0.75rem',
                                                fontWeight: '700',
                                                color: isFirst ? '#cbd5e1' : '#1e293b',
                                                backgroundColor: isFirst ? '#f8fafc' : '#f1f5f9',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                cursor: isFirst ? 'not-allowed' : 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                            title="Move Up"
                                        >
                                            <FaArrowUp size={11} /> Up
                                        </button>

                                        <button
                                            type="button"
                                            disabled={isLast}
                                            onClick={() => handleMoveItem(index, 'down')}
                                            style={{
                                                padding: '6px 10px',
                                                fontSize: '0.75rem',
                                                fontWeight: '700',
                                                color: isLast ? '#cbd5e1' : '#1e293b',
                                                backgroundColor: isLast ? '#f8fafc' : '#f1f5f9',
                                                border: '1px solid #cbd5e1',
                                                borderRadius: '6px',
                                                cursor: isLast ? 'not-allowed' : 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                            title="Move Down"
                                        >
                                            <FaArrowDown size={11} /> Down
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '14px 24px',
                    borderTop: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            padding: '9px 20px',
                            background: primaryColor,
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '700',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                    >
                        <FaCheck /> Done
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CategoryRearrangeModal;
